import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AppShell from '../components/AppShell.tsx';
import { ExperimentStatusWord } from '../components/StatusWord.tsx';
import { apiClient } from '../lib/api.ts';
import { conversionLabel, type Experiment } from '../../netlify/functions/_lib/domain.ts';

export default function DashboardPage() {
  const [experiments, setExperiments] = useState<Experiment[] | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    void apiClient
      .listExperiments()
      .then((data) => {
        if (!cancelled) setExperiments(data.experiments);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load experiments.');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const running = experiments?.filter((item) => item.status === 'running') ?? [];

  return (
    <AppShell>
      <main className="mx-auto max-w-5xl px-6 py-10">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-3xl">Experiments</h1>
            <p className="mt-2 max-w-prose text-ink-muted">
              One focused demand test at a time. A running experiment is a bet you have not decided
              yet.
            </p>
          </div>
          <Link
            to="/app/experiments/new"
            className="inline-flex rounded bg-accent-600 px-4 py-2 text-sm font-medium text-paper hover:bg-accent-700"
          >
            New experiment
          </Link>
        </div>

        {running.length > 1 ? (
          <p className="mt-6 rounded border border-border bg-pending-soft px-4 py-3 text-sm text-pending">
            You have {running.length} experiments in progress. The product works best when you
            finish one bet before starting the next.
          </p>
        ) : null}

        {error ? <p className="mt-6 text-danger">{error}</p> : null}

        {experiments && experiments.length === 0 ? (
          <section className="mt-10 rounded-xl border border-border bg-surface p-8 shadow-card">
            <h2 className="text-xl">Nothing running yet</h2>
            <p className="mt-2 max-w-prose text-ink-muted">
              Write the audience, the message, the offer, and the count that would change your next
              step. Then share one tracked link and record what actually happens.
            </p>
            <Link
              to="/app/experiments/new"
              className="mt-6 inline-flex rounded bg-accent-600 px-4 py-2 text-sm font-medium text-paper hover:bg-accent-700"
            >
              New experiment
            </Link>
          </section>
        ) : null}

        {experiments && experiments.length > 0 ? (
          <ul className="mt-8 divide-y divide-border rounded-xl border border-border bg-surface shadow-card">
            {experiments.map((item) => (
              <li key={item.id}>
                <Link
                  to={`/app/experiments/${item.id}`}
                  className="flex flex-col gap-2 px-5 py-4 hover:bg-surface-sunk sm:flex-row sm:items-center sm:justify-between"
                >
                  <div>
                    <p className="font-medium text-ink">{item.title}</p>
                    <p className="mt-1 text-sm text-ink-muted">
                      {conversionLabel(item.success_event_kind, item.success_label)} · count{' '}
                      <span className="font-mono">{item.success_threshold}</span>
                    </p>
                  </div>
                  <ExperimentStatusWord status={item.status} />
                </Link>
              </li>
            ))}
          </ul>
        ) : null}

        {experiments === null && !error ? (
          <p className="mt-8 text-ink-muted">Loading experiments…</p>
        ) : null}
      </main>
    </AppShell>
  );
}
